import { useState } from "react";
import { useTranslations } from "use-intl";
import { Button } from "@/components/ui/button";
import { ContextDialogShell } from "./ContextDialogShell";
import { ServiceRegistry } from "@/services/ServiceRegistry";
import { toast } from "sonner";
import { userFacingErrorMessage } from '@/utils/userFacingError';

interface ApprovalContextModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  suggestionId: string;
  selectedOptionId: string;
  onApprovalComplete: () => void;
}

export function ApprovalContextModal({
  open,
  onOpenChange,
  suggestionId,
  selectedOptionId,
  onApprovalComplete,
}: ApprovalContextModalProps) {
  const t = useTranslations("consultantReview.approval");
  const [approvalContext, setApprovalContext] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bookkeepingSuggestionService = ServiceRegistry.getBookkeepingSuggestionService();

  const handleApproveWithContext = async () => {
    if (!approvalContext.trim()) {
      setError(t("contextRequired"));
      return;
    }

    await submitApproval(approvalContext);
  };

  const handleSkipAndApprove = async () => {
    await submitApproval(undefined);
  };

  const submitApproval = async (context?: string) => {
    setIsSubmitting(true);
    setError(null);

    try {
      await bookkeepingSuggestionService.approveSuggestion(
        suggestionId,
        selectedOptionId,
        context
      );

      toast.success(t("approved"));

      setApprovalContext("");
      onApprovalComplete();
      onOpenChange(false);
    } catch (err) {
      console.error("Error approving suggestion:", err);
      setError(userFacingErrorMessage(err, t("failed")));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setApprovalContext("");
    setError(null);
    onOpenChange(false);
  };

  return (
    <ContextDialogShell
      open={open}
      onOpenChange={onOpenChange}
      title={t("title")}
      description={t("description")}
      placeholder={t("placeholder")}
      value={approvalContext}
      onValueChange={(value) => {
        setApprovalContext(value);
        setError(null);
      }}
      error={error}
      note={t("note")}
      footer={
        <>
          <Button variant="outline" onClick={handleCancel} disabled={isSubmitting}>
            {t("cancel")}
          </Button>
          <Button variant="secondary" onClick={handleSkipAndApprove} disabled={isSubmitting}>
            {isSubmitting ? t("approving") : t("skip")}
          </Button>
          <Button onClick={handleApproveWithContext} disabled={!approvalContext.trim() || isSubmitting}>
            {isSubmitting ? t("approving") : t("approve")}
          </Button>
        </>
      }
    />
  );
}
